import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, Ref } from "react";
import { useId, useState } from "react";
import { cn } from "../lib/cn";
import { EyeIcon, EyeOffIcon, PlusIcon } from "./icons";

/* ------------------------------------------------------------------ button */

type Variant = "primary" | "secondary" | "ghost" | "danger";

/**
 * No variant draws a border.
 *
 * That is the «Тихо» rule and it is what makes a secondary button read as
 * quieter rather than as a different kind of control: it is the same shape, one
 * tonal step off the surface it sits on.
 *
 * `text-ground` rather than `text-white`: ground is warm paper when the accent
 * is deep amber and near-black when the accent is bright amber, so the pair
 * stays legible in both themes without a `dark:` variant. `accent-ink` moves
 * the same way — darker than the accent in light, lighter in dark — which makes
 * it the right hover in both directions.
 */
const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-accent text-ground hover:bg-accent-ink disabled:bg-surface-alt disabled:text-muted",
  secondary: "bg-surface-alt text-ink hover:bg-line disabled:text-muted",
  ghost: "text-ink-soft hover:bg-surface-alt disabled:text-muted",
  danger: "bg-neg-soft text-neg hover:bg-neg hover:text-ground",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  full?: boolean;
}

export function Button({
  variant = "primary",
  full = false,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      className={cn(
        // min-h ties every button to the shared 44px touch floor. A pill rather
        // than a rounded rectangle: at 44px tall the difference is a rounding
        // error, and committing to it stops controls and cards from reading as
        // the same shape at two sizes.
        "inline-flex min-h-[var(--touch)] items-center justify-center gap-2 rounded-pill px-5",
        "text-[0.9375rem] font-semibold transition-colors select-none",
        "disabled:cursor-not-allowed",
        VARIANTS[variant],
        full && "w-full",
        className,
      )}
    />
  );
}

/* ------------------------------------------------------------------- field */

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string | undefined;
}

export function TextField({
  label,
  hint,
  error,
  className,
  ref,
  ...props
}: TextFieldProps & { ref?: Ref<HTMLInputElement> }) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="px-1 text-[0.8125rem] font-medium text-ink-soft">
        {label}
      </label>
      <input
        {...props}
        ref={ref}
        id={id}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        className={cn(
          // Filled rather than outlined: a border here would be the one drawn
          // line on the screen. The field is legible because it is a tonal step
          // off the surface behind it, and a ring appears only when something
          // is wrong — which is then the only ring in sight.
          "min-h-[var(--touch)] w-full rounded-row bg-surface-alt px-4 py-2.5",
          "placeholder:text-muted transition-colors outline-none",
          error && "ring-2 ring-neg",
          className,
        )}
      />
      {error ? (
        <p id={`${id}-error`} className="px-1 text-[0.8125rem] text-neg">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="px-1 text-[0.8125rem] text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A password field with a way to look at what you typed.
 *
 * Worth more here than in an ordinary app, and for a reason written into the
 * product: there is no password reset by mail and there is not going to be one
 * (SPEC §4.1). A typo made while registering is therefore discovered on the
 * next sign-in and fixed by a command on the server. One button spares that.
 *
 * Defaults to hidden, and says which state it is in out loud — the icon alone
 * is ambiguous about whether it shows what will happen or what is happening.
 */
export function PasswordField({
  label,
  showLabel,
  hideLabel,
  ...props
}: Omit<TextFieldProps, "type"> & { showLabel: string; hideLabel: string }) {
  const [shown, setShown] = useState(false);

  return (
    <div className="relative">
      <TextField
        {...props}
        label={label}
        type={shown ? "text" : "password"}
        // Room for the button, so a long password does not run under it.
        className="pr-[var(--touch)]"
      />
      <button
        type="button"
        onClick={() => setShown((was) => !was)}
        aria-label={shown ? hideLabel : showLabel}
        aria-pressed={shown}
        // Positioned against the field, not the label above or the hint below:
        // both are optional and would move it.
        className="absolute top-[1.75rem] right-0 grid size-[var(--touch)] place-items-center rounded-pill text-muted transition-colors hover:text-ink"
      >
        {shown ? (
          <EyeOffIcon className="size-[20px]" strokeWidth={1.8} />
        ) : (
          <EyeIcon className="size-[20px]" strokeWidth={1.8} />
        )}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------- card */

export function Card({
  children,
  className,
  ref,
}: {
  children: ReactNode;
  className?: string;
  /** So a caller can bring the card into view — see the space switcher. */
  ref?: Ref<HTMLElement>;
}) {
  return (
    <section
      ref={ref}
      className={cn(
        // Depth is tonal. The shadow is there for the light theme, where a
        // surface and its ground are close; in dark it is almost nothing and
        // the tonal step does the work on its own.
        "overflow-hidden rounded-card bg-surface shadow-soft",
        className,
      )}
    >
      {children}
    </section>
  );
}

/**
 * The «+» that starts a new one of whatever this screen is about.
 *
 * One definition, because four screens had four answers: the calendar had this
 * amber circle, the lists a button reading «Новый список», and the wishes and
 * the threads a form sitting open at the top of the screen forever — a
 * head-height box on a screen where most visits add nothing. An icon in the
 * heading is the smallest thing that can mean "another one", and it is in the
 * same place on every screen, which is the whole of the argument.
 *
 * Icon-only, so it needs a name out loud: `label` is both the accessible name
 * and the tooltip.
 */
export function AddButton({
  label,
  onClick,
  dimmed = false,
  className,
}: {
  label: string;
  onClick: () => void;
  /**
   * While the form it opens is already open.
   *
   * Dimmed rather than removed. Taking it out changes the width of the row it
   * sits in, so the heading beside it reflows at the exact moment a sheet is
   * sliding up over it — which reads as the screen blinking, and was blamed on
   * the sheet's own movement for a while. It stays where it is, at forty per
   * cent, and does nothing when pressed.
   */
  dimmed?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-hidden={dimmed || undefined}
      tabIndex={dimmed ? -1 : undefined}
      className={cn(
        "grid size-[var(--touch)] shrink-0 place-items-center rounded-pill bg-accent text-ground transition-[background-color,opacity] hover:bg-accent-ink",
        dimmed && "pointer-events-none opacity-40",
        className,
      )}
    >
      <PlusIcon className="size-[20px]" strokeWidth={2.1} />
    </button>
  );
}

export function CardHeader({
  title,
  meta,
  icon,
}: {
  title: string;
  meta?: ReactNode;
  icon?: ReactNode;
}) {
  // The one place a line survives: a hairline divider *inside* a surface, never
  // around one.
  return (
    <header className="flex items-center gap-3 border-b border-line-soft px-5 py-3.5">
      {icon ? <span className="text-lg leading-none">{icon}</span> : null}
      <h2 className="min-w-0 flex-1 truncate font-display text-[1.0625rem]">{title}</h2>
      {meta ? (
        <span className="tabular shrink-0 text-[0.8125rem] text-muted">{meta}</span>
      ) : null}
    </header>
  );
}
